import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { NavLightComponent } from '../../components/nav-light/nav-light.component';
import { KeyFeaturesComponent } from '../../components/key-features/key-features.component';
import { FooterOneComponent } from '../../components/footer/footer-one/footer-one.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { CountryService, Country } from '../../services/country.service';
import { RecaptchaService } from '../../services/recaptcha.service';
import { LandingService } from '../../services/landing.service';
import { ActivatedRoute, Router } from '@angular/router';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { environment } from 'apps/cricteams/src/environments/environment';
import { DyButtonComponent } from '../../components/form-fields/dy-button/dy-button.component';
import { ClubRegistrationComponent } from '../onboarding/club-registration/club-registration.component';
import { SeoService } from '../../services/seo.service';
import { StructuredDataService } from '../../services/structured-data.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { AnalyticsService } from '../../services/analytics.service';
import { analyticsEvents } from '../../constants/analytics';

@Component({
  selector: 'cricteams-app-landing',
  standalone: true,
  imports: [
    CommonModule,
    NavLightComponent,
    KeyFeaturesComponent,
    FooterOneComponent,
    ReactiveFormsModule,
    DyButtonComponent,
    ClubRegistrationComponent
],
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit {
  isActive = false;
  canShare = false;
  myForm: FormGroup;
  isLoading = false;
  formData = {
    name: '',
    email: '',
    country: '',
    state: '',
    clubName: '',
    reason: 'general',
    message: ''
  };
  countries: Country[] = [];
  states: string[] = [];
  slider: any;
  displayShareNotSupported = false;
  errorMessage = '';
  public regBtnLightClass =
  'border-[1px] bg-white text-blue text-2xl border-light-blue font-semibold px-3 h-[65px] sm:h-[100px] md:h-[65px] md:w-[250px] transition duration-700 ease-in-out rounded-md';
  public regBtnDarkClass =
  'text-white text-2xl bg-dark-blue font-semibold px-3 h-[65px] sm:h-[100px] md:h-[65px] md:w-[250px] transition duration-700 ease-in-out rounded-md';
  public demoBtnLightClass =
  'border-[2px] bg-transparent text-white text-2xl border-white font-semibold px-3 h-[65px] sm:h-[100px] md:h-[65px] md:w-[250px] transition duration-700 ease-in-out rounded-md hover:bg-white hover:text-indigo-600';
  public demoBtnDarkClass =
  'border-[2px] bg-transparent text-indigo-600 text-2xl border-indigo-600 font-semibold px-3 h-[65px] sm:h-[100px] md:h-[65px] md:w-[250px] transition duration-700 ease-in-out rounded-md hover:bg-indigo-600 hover:text-white';
  @ViewChild('registrationPopup') registrationPopup!: ClubRegistrationComponent;
  public showRegistrationPopup = false;
  private originalUrl: string | null = null;
  constructor(
    private landingService: LandingService,
    private recaptchaService: RecaptchaService,
    private countryService: CountryService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private seoService: SeoService,
    private structuredDataService: StructuredDataService,
    private maintenanceService: MaintenanceService,
    private analyticsService: AnalyticsService
  ) {
    this.myForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      state: ['', Validators.required],
      clubName: ['', Validators.required],
      reason: ['general'],
      message: ['', Validators.required],
    });
    this.canShare = typeof navigator !== 'undefined' && !!navigator.share;
  }

  async ngOnInit(): Promise<void> {
    // Check maintenance status — redirect to pre-dashboard if SPA maintenance is ON
    const maintenanceStatus = await this.maintenanceService.getPublicStatus();
    if (maintenanceStatus?.isSpaEnabled) {
      this.router.navigateByUrl('/pre-dashboard');
      return;
    }

    // Set SEO meta tags based on current route
    const currentUrl = this.router.url;
    let seoConfig: any;
    
    if (currentUrl.startsWith('/register-club')) {
      seoConfig = {
        title: 'Register Your Cricket Club - CrickTeams',
        description: 'Register your cricket club on CrickTeams. Get started with our comprehensive cricket team management platform. Free club registration for cricket teams, leagues, and tournaments.',
        keywords: 'register cricket club, cricket club registration, join crickteams, cricket team signup, create cricket club',
        url: 'https://www.crickteams.com/register-club',
        type: 'website',
      };
    } else if (currentUrl.startsWith('/key-features')) {
      seoConfig = {
        title: 'Key Features - Cricket Team Management | CrickTeams',
        description: 'Explore CrickTeams powerful features for managing cricket teams, tournaments, matches, and players. Comprehensive tools for cricket clubs and leagues.',
        keywords: 'cricket management features, cricket team tools, cricket tournament features, cricket match management',
        url: 'https://www.crickteams.com/key-features',
        type: 'website',
      };
    } else if (currentUrl.startsWith('/contact-us')) {
      seoConfig = {
        title: 'Contact Us - CrickTeams',
        description: 'Get in touch with CrickTeams. Have questions about our cricket team management platform? Contact our support team for help.',
        keywords: 'contact crickteams, cricket team support, crickteams help, cricket management support',
        url: 'https://www.crickteams.com/contact-us',
        type: 'website',
      };
    } else {
      // Default for / and /home - use canonical as /
      seoConfig = {
        title: 'CrickTeams - #1 Cricket Team & Club Management Platform',
        description: 'The #1 cricket team and club management platform. Manage tournaments, matches, players, and finances with CrickTeams. Free forever. No credit card required.',
        keywords: 'cricket team management, cricket club management, cricket management software, cricket team management app, cricket tournaments, cricket leagues, cricket clubs, sports management, cricket matches, cricket players',
        url: 'https://www.crickteams.com/',
        type: 'website',
      };
    }
    
    this.seoService.updateTags(seoConfig);
    this.analyticsService.trackEvent(analyticsEvents.HOME_PAGE_VIEW, {
      page: currentUrl,
    });

    // Add breadcrumbs
    this.structuredDataService.addBreadcrumbs([
      { name: 'Home', url: 'https://www.crickteams.com/' },
    ]);
    
    this.countryService.getCountries().subscribe(countries => {
      this.countries = countries;
      // Auto-select US based on timezone
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz.startsWith('America/') || tz.startsWith('US/')) {
        this.myForm.patchValue({ country: 'US' });
        this.onCountryChange('US');
      }
    });
    this.route.data.subscribe(data => {
      if (data['section']) {
        setTimeout(() => {
          this.scrollToSection(data['section']);
        }, 100);
      }
      
      // Check if we should open the registration modal
      if (data['openModal']) {
        setTimeout(() => {
          this.openModal();
        }, 500); // Delay to ensure component is fully loaded
      }
    });
  }

    // Add a method to scroll to a section
    scrollToSection(sectionId: string) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

  onCountryChange(countryCode: string) {
    this.states = [];
    this.myForm.patchValue({ state: '' });
    if (countryCode) {
      this.countryService.getStatesByCountry(countryCode).subscribe(states => {
        this.states = states;
      });
    }
  }

  onSubmit() {
    if (this.myForm.valid) {
      this.isLoading = true;
      this.formData = this.myForm.value;
      this.errorMessage = '';
      this.recaptchaService
        .executeRecaptcha('submit')
        .then(async (token: string) => {
          console.log('reCAPTCHA token:', token);
          const isValid = await this.verifyToken(token);
          if (isValid) {
           await this.submitContactForm();
           this.isLoading = false;
          } else {
            console.error('reCAPTCHA error : invalid captcha token');
          }

          // Send the token to your backend for verification
        })
        .catch((error) => {
          console.error('reCAPTCHA error:', error);
          this.isLoading = false;
        });
    } else {
      this.errorMessage = 'Name, Email, Country and State are required fields';
      console.log('Invalid Form');
      this.isLoading = false;
    }
  }

  closeModal(): void {
    this.isActive = false;
    // Refresh the page
    window.location.reload();
  }

  share(): void {
    if (this.canShare) {
      this.displayShareNotSupported = false;
      navigator
        .share({
          title: 'Check out this!',
          text: 'Here is all-in-one cricket team management platform i found.',
          url: environment.baseUrl,
        })
        .then(() => {
          console.log('Content shared successfully!');
        })
        .catch((error) => {
          console.error('Error sharing content:', error);
        });
    } else {
      this.displayShareNotSupported = true;
      console.error('share not supported');
    }
  }
  async verifyToken(token: string): Promise<boolean> {
    try {
      const response = await this.recaptchaService.verifyCaptcha(token);
      if (response) {
        this.errorMessage = '';
        console.log('reCAPTCHA validation successful');
        // Proceed with your logic
      } else {
        this.errorMessage = 'reCAPTCHA validation failed';
        console.log('reCAPTCHA validation failed');
        // Handle validation failure
      }
      return response;
    } catch (error) {
      console.error('Error verifying reCAPTCHA:', error);
      // Handle HTTP error
      return false;
    }
  }

  openModal(): void {
    this.registrationPopup.showPopup();
  }

  navigateToRegister(): void {
    // Check if we're already on the landing page
    const isOnLanding = this.router.url === '/' || 
                        this.router.url === '/home' || 
                        this.router.url === '/register-club' ||
                        this.router.url.startsWith('/contact-us') ||
                        this.router.url.startsWith('/key-features');
    
    if (isOnLanding) {
      // Store original URL before changing it
      if (this.router.url !== '/register-club') {
        this.originalUrl = this.router.url;
      }
      // Update URL without navigation to prevent scrolling
      this.location.replaceState('/register-club');
      this.openModal();
    } else {
      // Full navigation from a different page
      this.originalUrl = null; // Clear since we're doing full navigation
      this.router.navigate(['/register-club']);
    }
  }

  closeRegModal(): void {
    this.showRegistrationPopup = false;
    // If we have an original URL, restore it without navigation
    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
      this.originalUrl = null;
    } else if (this.router.url === '/register-club') {
      // Full navigation back to home if no original URL tracked
      this.router.navigate(['/home']);
    }
    // If we're on / or /home or section routes, just close the modal
  }

  navigateToBookDemo(): void {
    this.analyticsService.trackEvent(analyticsEvents.BOOK_DEMO_CLICK, {
      source: 'landing_page',
      url: environment.bookDemoUrl,
    });
    window.open(environment.bookDemoUrl, '_blank');
  }

  async submitContactForm() {
    const submitResponse = await this.landingService.submitContactForm(this.formData)
    if (submitResponse.success) {
      this.isActive = true;
      this.analyticsService.trackEvent(analyticsEvents.CONTACT_FORM_SUBMIT, {
        source: 'landing_page',
        form_type: this.formData.reason,
      });
    } else {
      this.errorMessage = 'Error submitting form, please try again';
    }
  }
}
